import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaCashRegister, FaTimes } from 'react-icons/fa';
import instanciaAxios, { esErrorPermiso } from '../../miscelaneo/instanciaAxios';
import { useContextoSesion } from '../../miscelaneo/ContextoSesion';
import { useImpresion } from '../../contexts/ContextoImpresion';
import SpinnerCargando from '../util/SpinnerCargando';

// Denominaciones RD$
const DENOMINACIONES = [2000, 1000, 500, 200, 100, 50, 25, 10, 5, 1];

export default function CierreCaja() {
  const navigate = useNavigate();
  const { sesionCaja, setSesionCaja, sesionCajaCargada, cargando: cargandoContexto } = useContextoSesion();
  const { imprimirDocumento } = useImpresion();

  const [resumen, setResumen] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [cerrando, setCerrando] = useState(false);

  // Ref para evitar redirect después de cerrar caja exitosamente
  const cierreExitosoRef = useRef(false);

  // Desglose de efectivo
  const [desglose, setDesglose] = useState(
    DENOMINACIONES.map(d => ({ denominacion: d, cantidad: 0, subtotal: 0 }))
  );

  // Montos reales por método de pago
  const [montosReales, setMontosReales] = useState({});

  // Fondo siguiente y comentario
  const [fondoSiguiente, setFondoSiguiente] = useState('0');
  const [comentarioCierre, setComentarioCierre] = useState('');

  // Esperar a que el contexto cargue y luego decidir
  useEffect(() => {
    // No hacer nada si se cerró la caja exitosamente
    if (cierreExitosoRef.current) return;

    // Esperar a que la sesión de caja haya sido verificada
    if (cargandoContexto || !sesionCajaCargada) return;

    if (!sesionCaja) {
      toast.warning('No tienes una caja abierta');
      navigate('/AperturaCaja');
      return;
    }

    cargarResumen();
  }, [sesionCaja, sesionCajaCargada, cargandoContexto]);

  const cargarResumen = async () => {
    if (!sesionCaja?.id_sesion_caja) return;
    try {
      setCargando(true);
      const response = await instanciaAxios.get(`/sesion-caja/${sesionCaja.id_sesion_caja}/resumen`);
      setResumen(response.data);

      // Inicializar montos reales por GRUPO (categoría general)
      const reales = {};
      const gruposVistos = new Set();
      (response.data.metodos_pago_disponibles || []).forEach(mp => {
        const cat = clasificarMetodo(mp.metodo_pago);
        if (!gruposVistos.has(cat)) {
          gruposVistos.add(cat);
          reales[cat] = '';
        }
      });
      setMontosReales(reales);
    } catch (error) {
      if (esErrorPermiso(error)) return;
      console.error('Error al cargar resumen:', error);
      toast.error('Error al cargar el resumen de la sesión');
    } finally {
      setCargando(false);
    }
  };

  // Actualizar desglose de efectivo
  const actualizarDesglose = (index, cantidad) => {
    const nuevoDesglose = [...desglose];
    const cant = parseInt(cantidad) || 0;
    nuevoDesglose[index] = {
      ...nuevoDesglose[index],
      cantidad: cant,
      subtotal: cant * nuevoDesglose[index].denominacion
    };
    setDesglose(nuevoDesglose);
  };

  // Total de efectivo contado
  const totalEfectivoContado = useMemo(() => {
    return desglose.reduce((sum, d) => sum + d.subtotal, 0);
  }, [desglose]);

  // Helper: clasificar un método de pago en su categoría general
  const clasificarMetodo = (nombre) => {
    const n = (nombre || '').toLowerCase();
    if (n.includes('efectivo')) return 'Efectivo';
    if (n.includes('tarjeta')) return 'Tarjeta';
    if (n.includes('transferencia')) return 'Transferencia';
    if (n.includes('cheque')) return 'Cheque';
    return nombre; // Si no encaja, usar nombre original
  };

  // Calcular montos esperados por CATEGORÍA de método de pago
  // Agrupa: "Tarjeta de Crédito" + "Tarjeta de Débito" → "Tarjeta"
  const { gruposMetodo, metodosPorGrupo } = useMemo(() => {
    if (!resumen) return { gruposMetodo: [], metodosPorGrupo: {} };

    // 1. Calcular esperado individual por cada método de pago
    const metodos = {};
    (resumen.metodos_pago_disponibles || []).forEach(mp => {
      metodos[mp.id_metodo_pago] = {
        id: mp.id_metodo_pago,
        nombre: mp.metodo_pago,
        esperado: 0
      };
    });

    (resumen.ingresos || []).forEach(m => {
      if (!metodos[m.id_metodo_pago]) {
        metodos[m.id_metodo_pago] = { id: m.id_metodo_pago, nombre: m.metodo_pago, esperado: 0 };
      }
      metodos[m.id_metodo_pago].esperado += parseFloat(m.total);
    });

    (resumen.egresos_compra || []).forEach(m => {
      if (!metodos[m.id_metodo_pago]) {
        metodos[m.id_metodo_pago] = { id: m.id_metodo_pago, nombre: m.metodo_pago, esperado: 0 };
      }
      metodos[m.id_metodo_pago].esperado -= parseFloat(m.total);
    });

    (resumen.egresos_gasto || []).forEach(m => {
      if (m.id_metodo_pago) {
        if (!metodos[m.id_metodo_pago]) {
          metodos[m.id_metodo_pago] = { id: m.id_metodo_pago, nombre: m.metodo_pago, esperado: 0 };
        }
        metodos[m.id_metodo_pago].esperado -= parseFloat(m.total);
      }
    });

    // Para efectivo: sumar fondo de apertura y restar retiros
    const metodoEfectivo = Object.values(metodos).find(m => m.nombre?.toLowerCase().includes('efectivo'));
    if (metodoEfectivo && resumen.sesion) {
      metodoEfectivo.esperado += parseFloat(resumen.sesion.monto_apertura) || 0;
      metodoEfectivo.esperado -= parseFloat(resumen.totales?.total_retiros) || 0;
    }

    // 2. Agrupar por categoría general
    const grupos = {};
    const porGrupo = {};

    Object.values(metodos).forEach(m => {
      const categoria = clasificarMetodo(m.nombre);
      if (!grupos[categoria]) {
        grupos[categoria] = { nombre: categoria, esperado: 0 };
        porGrupo[categoria] = [];
      }
      grupos[categoria].esperado += m.esperado;
      porGrupo[categoria].push(m);
    });

    // Orden fijo: Efectivo primero, luego Tarjeta, Transferencia, Cheque, y el resto
    const ordenFijo = ['Efectivo', 'Tarjeta', 'Transferencia', 'Cheque'];
    const gruposArr = [];
    ordenFijo.forEach(cat => {
      if (grupos[cat]) gruposArr.push(grupos[cat]);
    });
    // Agregar cualquier otro que no esté en el orden fijo
    Object.keys(grupos).forEach(cat => {
      if (!ordenFijo.includes(cat)) gruposArr.push(grupos[cat]);
    });

    return { gruposMetodo: gruposArr, metodosPorGrupo: porGrupo };
  }, [resumen]);

  const formatMonto = (monto) => {
    return parseFloat(monto || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const cerrarCaja = async () => {
    try {
      setCerrando(true);

      // Construir detalle de métodos de pago expandido (un registro por método individual)
      const metodos_pago = [];

      gruposMetodo.forEach(grupo => {
        const esEfectivo = grupo.nombre === 'Efectivo';
        const montoRealGrupo = esEfectivo
          ? totalEfectivoContado
          : parseFloat(montosReales[grupo.nombre]) || 0;
        const subMetodos = metodosPorGrupo[grupo.nombre] || [];

        if (subMetodos.length === 1) {
          // Solo un método en este grupo: asignar directo
          const m = subMetodos[0];
          metodos_pago.push({
            id_metodo_pago: m.id,
            monto_esperado: parseFloat(m.esperado.toFixed(2)),
            monto_real: parseFloat(montoRealGrupo.toFixed(2)),
            diferencia: parseFloat((montoRealGrupo - m.esperado).toFixed(2)),
            referencia_externa: null
          });
        } else {
          // Múltiples métodos (ej: Tarjeta Crédito + Débito): distribuir proporcionalmente
          const totalEsperadoGrupo = subMetodos.reduce((sum, m) => sum + m.esperado, 0);

          subMetodos.forEach(m => {
            let montoRealIndividual;
            if (totalEsperadoGrupo > 0) {
              // Distribuir proporcionalmente al esperado
              montoRealIndividual = (m.esperado / totalEsperadoGrupo) * montoRealGrupo;
            } else {
              // Si no hay esperado, dividir equitativamente
              montoRealIndividual = montoRealGrupo / subMetodos.length;
            }
            metodos_pago.push({
              id_metodo_pago: m.id,
              monto_esperado: parseFloat(m.esperado.toFixed(2)),
              monto_real: parseFloat(montoRealIndividual.toFixed(2)),
              diferencia: parseFloat((montoRealIndividual - m.esperado).toFixed(2)),
              referencia_externa: null
            });
          });
        }
      });

      // Filtrar desglose con cantidades > 0
      const desglose_efectivo = desglose.filter(d => d.cantidad > 0);

      const response = await instanciaAxios.post(`/sesion-caja/${sesionCaja.id_sesion_caja}/cerrar`, {
        desglose_efectivo,
        metodos_pago,
        monto_fondo_siguiente: parseFloat(fondoSiguiente) || 0,
        comentario_cierre: comentarioCierre || null
      });

      // Marcar como cierre exitoso ANTES de limpiar sesionCaja
      cierreExitosoRef.current = true;
      toast.success(response.data.mensaje || 'Caja cerrada exitosamente');

      try {
        await imprimirDocumento({
          idDocumento: sesionCaja.id_sesion_caja,
          tipoDocumento: 'cierre_caja',
          formato: 'continuo'
        });
      } catch (printErr) {
        console.warn('No se pudo imprimir cierre de caja:', printErr?.message);
      }

      setSesionCaja(null);
      navigate('/Dashboard');
    } catch (error) {
      if (esErrorPermiso(error)) return;
      console.error('Error al cerrar caja:', error);
      toast.error(error.response?.data?.mensaje || 'Error al cerrar la caja');
    } finally {
      setCerrando(false);
    }
  };

  // Mostrar spinner mientras carga contexto o resumen
  if (cargandoContexto || !sesionCajaCargada || cargando) return <SpinnerCargando />;
  if (!sesionCaja && !cierreExitosoRef.current) return null; // Va a redirigir en el useEffect
  if (!resumen) return <p className="text-center text-gray-500 py-8">No se pudo cargar el resumen</p>;

  return (
    <div className="bg-gray-200 min-h-screen">
      <div className="flex justify-center py-10">
        <div className="bg-gray-100 bg-opacity-90 rounded-xl border border-gray-100 p-12 w-full max-w-7xl mx-10">
          <div className="flex flex-col gap-8">
            {/* Header */}
            <div className="space-y-1">
              <h1 className="font-cascadia font-bold text-2xl">Cierre de Caja</h1>
            </div>

            {/* Retiros de Caja (si hay) */}
            {parseFloat(resumen.totales?.total_retiros) > 0 && (
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-800">Retiros de Caja realizados en esta sesión</p>
                  <p className="text-xs text-purple-600">{resumen.retiros?.length || 0} retiro(s) registrado(s)</p>
                </div>
                <p className="text-xl font-bold text-purple-700">
                  RD$ {parseFloat(resumen.totales.total_retiros).toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                </p>
              </div>
            )}

            {/* Desglose de Efectivo - Tabla Vertical */}
            <div className="bg-white rounded-xl border border-gray-300 shadow-md p-6">
              <h2 className="font-cascadia text-lg font-semibold text-gray-700 mb-4">Desglose de Efectivo</h2>
              
              <div className="border border-gray-400 rounded overflow-hidden">
                <table className="w-full text-sm font-mono">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left py-1.5 px-3 border-b border-r border-gray-400 font-semibold text-gray-700">Denominación</th>
                      <th className="text-center py-1.5 px-3 border-b border-r border-gray-400 font-semibold text-gray-700 w-32">Cantidad</th>
                      <th className="text-right py-1.5 px-3 border-b border-gray-400 font-semibold text-gray-700">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {desglose.map((d, index) => (
                      <tr key={d.denominacion} className="hover:bg-gray-50">
                        <td className="py-1 px-3 border-b border-r border-gray-300">RD$ {d.denominacion.toLocaleString('es-DO')}</td>
                        <td className="py-1 px-3 border-b border-r border-gray-300 text-center">
                          <input
                            type="number"
                            min="0"
                            value={d.cantidad || ''}
                            onChange={(e) => actualizarDesglose(index, e.target.value)}
                            className="w-16 px-1 py-0.5 border border-gray-400 text-center text-sm font-mono bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder="0"
                          />
                        </td>
                        <td className="py-1 px-3 border-b border-gray-300 text-right font-medium">
                          RD$ {d.subtotal.toLocaleString('es-DO', {minimumFractionDigits: 2})}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-100">
                      <td className="py-2 px-3 border-r border-gray-400 font-bold text-gray-900" colSpan="2">Total Efectivo Contado</td>
                      <td className="py-2 px-3 text-right font-bold text-gray-900">
                        RD$ {totalEfectivoContado.toLocaleString('es-DO', {minimumFractionDigits: 2})}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Cuadre por Método de Pago - Sin monto esperado */}
            {gruposMetodo.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-300 shadow-md p-6">
                <h2 className="font-cascadia text-lg font-semibold text-gray-700 mb-4">Cuadre por Método de Pago</h2>
                
                <div className="border border-gray-400 rounded overflow-hidden">
                  <table className="w-full text-sm font-mono">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="text-left py-1.5 px-3 border-b border-r border-gray-400 font-semibold text-gray-700">Método</th>
                        <th className="text-center py-1.5 px-3 border-b border-gray-400 font-semibold text-gray-700">Monto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gruposMetodo.map(grupo => {
                        const esEfectivo = grupo.nombre === 'Efectivo';
                        
                        return (
                          <tr key={grupo.nombre} className="hover:bg-gray-50">
                            <td className="py-1.5 px-3 border-b border-r border-gray-300 font-medium">{grupo.nombre}</td>
                            <td className="py-1.5 px-3 border-b border-gray-300 text-center">
                              {esEfectivo ? (
                                <span className="text-gray-600">
                                  RD$ {formatMonto(totalEfectivoContado)}
                                </span>
                              ) : (
                                <input
                                  type="number"
                                  value={montosReales[grupo.nombre] || ''}
                                  onChange={(e) => setMontosReales({...montosReales, [grupo.nombre]: e.target.value})}
                                  className="w-36 px-2 py-0.5 border border-gray-400 text-center text-sm font-mono bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  placeholder="0.00"
                                  min="0"
                                  step="0.01"
                                />
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Fondo siguiente y comentario */}
            <div className="bg-white rounded-xl border border-gray-300 shadow-md p-6">
              <h2 className="font-cascadia text-lg font-semibold text-gray-700 mb-4">Opciones de Cierre</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Fondo para la siguiente caja (RD$)
                  </label>
                  <input
                    type="number"
                    value={fondoSiguiente}
                    onChange={(e) => setFondoSiguiente(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Comentario de cierre (opcional)
                  </label>
                  <textarea
                    value={comentarioCierre}
                    onChange={(e) => setComentarioCierre(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    rows="2"
                    placeholder="Observaciones del cierre..."
                  />
                </div>
              </div>

              {/* Botón de cierre */}
              <button
                onClick={cerrarCaja}
                disabled={cerrando}
                className="w-full mt-6 px-6 py-4 bg-red-600 text-white rounded-xl font-semibold text-lg hover:bg-red-700 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {cerrando ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                    Cerrando...
                  </>
                ) : (
                  <>
                    <FaTimes />
                    Cerrar Caja
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
